import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { classNames } from '@bambi/component-library/src/utils/classNames';
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  UserMinusIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { DateTime } from 'luxon';

import { AppointmentTimeDisplay } from 'src/common/date/AppointmentTimeDisplay';
import {
  DispatchTripPassengerRead,
  DispatchTripRead,
  PatchedTripPassengerRead,
  RunBambiRunReassignedTripRead,
  RunBambiRunTripPassengerRead,
  RunBambiRunTripRead,
  TripRead,
} from 'src/common/external/bambi-api/bambiApi';
import { getTripDriverName } from 'src/common/external/bambi-api/wrappers/trip/getTripDriverName';
import { getTripDropoffAddress } from 'src/common/external/bambi-api/wrappers/trip/getTripDropoffAddress';
import { getTripPickupAddress } from 'src/common/external/bambi-api/wrappers/trip/getTripPickupAddress';
import { isActivatedWillCall } from 'src/common/external/bambi-api/wrappers/trip/isActivatedWillCall';
import { isNonActivatedWillCall } from 'src/common/external/bambi-api/wrappers/trip/isNonActivatedWllCall';
import { MultiLoadIcon } from 'src/common/primitives/icon/MultiLoadIcon';
import colors from 'src/common/theme/colors';
import {
  formatFullName,
  formatFullNameIgnoreDeletedAt,
  formatPassengerFullName,
} from 'src/common/util/formatFullName';
import { TripSpaceTypeBadge } from 'src/features/trip/management/TripSpaceTypeBadge';

import { isDispatchTrip } from '../../common/isDispatchTrip';
import { TripDetailsPopover } from '../TripDetailsPopover/TripDetailsPopover';
import TripTagDisplay from '../TripTagDisplay';
import { TripCardDropdownMenu } from './TripDropdownMenu';

const BASE_CLASSES =
  'relative block flex-grow rounded-lg border shadow-sm focus:outline-none bg-white';
const WILL_CALL_CLASSES =
  'bg-trip-willcall-bg border-trip-willcall-border border-2';
const REQUESTED_CLASSES = 'bg-trip-requested-bg';
const PULSE_CLASSES = 'animate-pulse bg-purple-200';
const PULSE_CANCELLATION_REQUESTED = 'animate-pulse';

interface TripCardProps {
  trip:
    | DispatchTripRead
    | RunBambiRunTripRead
    | RunBambiRunReassignedTripRead
    | TripRead;
  hideMenu?: boolean;
  variant?:
    | 'default'
    | 'pickup'
    | 'dropoff'
    | 'route-template'
    | 'multi-load'
    | 'thin';
  adornment?: React.ReactNode;
  additionalPassengers?: number;
}

export function TripCardInternal({
  trip,
  hideMenu = false,
  variant = 'default',
  adornment,
  additionalPassengers,
}: TripCardProps) {
  const pulseWillCall = shouldPulseWillCall(trip);
  const tripPickupAddress = getTripPickupAddress(trip);
  const tripDropoffAddress = getTripDropoffAddress(trip);
  const tripDriverName = getTripDriverName(trip);
  const isWillCallTrip =
    isNonActivatedWillCall(trip) || isActivatedWillCall(trip);
  const pulseCancellationRequest = shouldPulseCancellationRequest(trip);

  const isPassengerDeleted = (
    passenger:
      | DispatchTripPassengerRead
      | RunBambiRunTripPassengerRead
      | PatchedTripPassengerRead
  ): boolean => {
    return 'deleted_at' in passenger && !!passenger.deleted_at;
  };

  return (
    <div
      className={classNames(
        BASE_CLASSES,
        pulseWillCall && PULSE_CLASSES,
        pulseCancellationRequest && PULSE_CANCELLATION_REQUESTED,
        trip.status === 'requested' && REQUESTED_CLASSES,
        isWillCallTrip && WILL_CALL_CLASSES,
        variant === 'multi-load' && 'border-orange-200'
      )}
      data-testid={`trip-card-${trip.id}`}
      style={
        pulseCancellationRequest
          ? {
              backgroundColor: colors.trip.canceled.bg,
            }
          : undefined
      }
    >
      <div
        className={`px-3 py-2 ${
          !hideMenu && !('assignment' in trip) ? 'pl-0' : ''
        }`}
      >
        <div className="flex-grow items-center justify-between sm:flex sm:gap-x-2">
          <div className="flex items-center">
            {!hideMenu && !('assignment' in trip) && (
              <div>
                <TripCardDropdownMenu trip={trip} />
              </div>
            )}
            <div className="flex flex-col gap-y-1">
              <div className="flex items-center gap-x-2">
                {(variant === 'default' ||
                  variant === 'route-template' ||
                  variant === 'multi-load' ||
                  variant === 'thin') && (
                  <div>
                    <Badge
                      label={DateTime.fromISO(
                        trip.scheduled_pickup_at
                      ).toLocaleString(DateTime.TIME_SIMPLE)}
                      color="blue-dark"
                    />
                  </div>
                )}
                {variant === 'dropoff' && (
                  <div title="dropoff" data-testid="trip-card-dropoff-icon">
                    <ArrowDownCircleIcon className="w-6 text-trip-complete-border" />
                  </div>
                )}
                {variant === 'pickup' && (
                  <div title="pickup" data-testid="trip-card-pickup-icon">
                    <ArrowUpCircleIcon className="w-6 text-trip-active-border" />
                  </div>
                )}
                <div title={formatPassengerFullName(trip.passenger)}>
                  {formatFullNameIgnoreDeletedAt(trip.passenger)}
                </div>
                {isPassengerDeleted(trip.passenger) && (
                  <span
                    className="inline-flex items-center rounded-full bg-red-100 p-0.5"
                    title="Passenger deleted"
                    data-testid="trip-card-passenger-deleted-icon"
                  >
                    <UserMinusIcon className="h-4 w-4 text-red-600" />
                  </span>
                )}
                {variant === 'multi-load' && (
                  <div
                    title="Multi-loaded trip"
                    data-testid="multi-load-trip-icon"
                    style={{
                      backgroundColor: 'transparent',
                      borderColor: 'black',
                      borderWidth: '1px',
                    }}
                    className="rounded-full p-0.5"
                  >
                    <MultiLoadIcon color="black" />
                  </div>
                )}
              </div>
              {(variant === 'route-template' || variant === 'multi-load') && (
                <div className="flex flex-row items-center gap-2">
                  <TripSpaceTypeBadge spaceType={trip.space_type} />
                  {additionalPassengers ? (
                    <div className="flex flex-row text-xs">
                      <UsersIcon className="w-4" />+{additionalPassengers}{' '}
                      Passengers
                    </div>
                  ) : null}
                </div>
              )}
              <div>
                <p
                  className={`m-0 inline text-left text-xs font-normal leading-5 text-[rgba(102,112,133,1)]`}
                >
                  {(variant === 'default' ||
                    variant === 'route-template' ||
                    variant === 'multi-load' ||
                    variant === 'thin') &&
                    `${tripPickupAddress} --> ${tripDropoffAddress}`}
                  {variant === 'pickup' && `${tripPickupAddress}`}
                  {variant === 'dropoff' && `${tripDropoffAddress}`}
                </p>
              </div>
              {trip.appointment_at && (
                <AppointmentTimeDisplay
                  textClass="text-sm"
                  strDate={trip.appointment_at}
                />
              )}
              {isDispatchTrip(trip) && trip.tags?.length ? (
                <div className="mt-2">
                  <TripTagDisplay tags={trip.tags} />
                </div>
              ) : null}
            </div>
          </div>
          {adornment ? (
            adornment
          ) : (
            <div className="flex flex-col gap-1">
              {/* TODO: Pretty sure there's a Vehicle/SpaceType badge component */}
              {/* If not, we should create one that's shared between this and the Trip and Fleet views */}
              <Badge label={trip.space_type} color="primary" />
              {trip.status === 'canceled' && (
                <Badge label="canceled" color="red" />
              )}
              <div>
                {'old_assignment' in trip &&
                  // Old assignment might not actually exist
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  !!trip.old_assignment?.driver && (
                    <div className="text-xs line-through opacity-[.75]">
                      {formatFullName(trip.old_assignment.driver)} -
                    </div>
                  )}

                {tripDriverName && (
                  <div className="text-xs">{tripDriverName}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {trip.status === 'requested' && (
        <div className="w-full bg-trip-requested-border px-3 py-2 pl-5 text-sm">
          {'payer' in trip && trip.payer.display_name
            ? trip.payer.display_name
            : 'Requested'}
        </div>
      )}
    </div>
  );
}

export function TripCard({ trip, ...props }: TripCardProps) {
  return (
    <TripDetailsPopover trip={trip}>
      <TripCardInternal trip={trip} {...props} />
    </TripDetailsPopover>
  );
}

function shouldPulseWillCall(
  trip:
    | DispatchTripRead
    | RunBambiRunTripRead
    | RunBambiRunReassignedTripRead
    | TripRead
) {
  return trip.status === 'pending' && isActivatedWillCall(trip);
}

function shouldPulseCancellationRequest(
  trip:
    | DispatchTripRead
    | RunBambiRunTripRead
    | RunBambiRunReassignedTripRead
    | TripRead
) {
  return (
    trip.status !== 'canceled' &&
    'is_cancellation_requested' in trip &&
    trip['is_cancellation_requested']
  );
}
